import { HttpClient } from '@adapters/HttpClient';
import { normalizeInvoicesSortColumn } from '../constants/invoicesSortableColumns';
import { Invoice } from '../models/Invoices';

export interface InvoicesFilters {
  searchFilter?: { filters: any[]; logic: string };
  search?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface InvoicesPage {
  invoices: Invoice[];
  nextPage?: number;
  hasMore: boolean;
  total: number;
}

interface GetInvoicesResponse {
  data: Invoice[];
  meta: {
    total: number;
    perPage: number;
    currentPage: number;
    lastPage: number;
  };
  links: {
    first: string;
    prev: string | null;
    next: string | null;
    last: string;
  };
}

function buildAdvancedFiltersParams(searchFilter: { filters: any[]; logic: string }) {
  const params: Record<string, any> = {};

  if (searchFilter.filters && Array.isArray(searchFilter.filters)) {
    searchFilter.filters.forEach((filter, index) => {
      params[`advanced_filters[filters][${index}][column]`] = filter.column;
      params[`advanced_filters[filters][${index}][operator]`] = filter.operator ?? filter.action;

      if (
        filter.value !== undefined &&
        filter.value !== null &&
        !(Array.isArray(filter.value) && filter.value.length === 0)
      ) {
        params[`advanced_filters[filters][${index}][value]`] = filter.value;
      }
    });
  }

  if (searchFilter.logic) {
    params['advanced_filters[logic]'] = searchFilter.logic;
  }

  return params;
}

export class InvoicesApiAdapter {
  constructor(private httpClient: HttpClient) {}

  async getInvoicesPaginated(filters: InvoicesFilters = {}): Promise<InvoicesPage> {
    const { searchFilter, search, sortBy = 'id', sortDirection = 'desc', page = 1, limit = 25 } = filters;

    const sortColumn = normalizeInvoicesSortColumn(sortBy);

    const params: Record<string, any> = {
      page,
      per_page: limit,
      'sorting[column]': sortColumn,
      'sorting[direction]': sortDirection,
      ...(search ? { search } : {}),
      ...(searchFilter ? buildAdvancedFiltersParams(searchFilter) : {}),
    };

    const response = await this.httpClient.get<GetInvoicesResponse>('/invoices', params, {}, page === 1);

    return {
      invoices: response.data,
      nextPage: response.meta.currentPage < response.meta.lastPage ? response.meta.currentPage + 1 : undefined,
      hasMore: response.meta.currentPage < response.meta.lastPage,
      total: response.meta.total,
    };
  }

  /**
   * Obtiene facturas sin erp_id (pendientes de generar en Holded)
   * GET /api/v1/invoices?advanced_filters[filters][0][column]=erp_id&advanced_filters[filters][0][operator]=is_null&advanced_filters[logic]=AND
   */
  async getPendingHoldedInvoices(): Promise<{ data: Invoice[] }> {
    const searchFilter = {
      filters: [
        {
          column: 'erp_id',
          operator: 'is_null',
        },
      ],
      logic: 'AND',
    };

    const params = buildAdvancedFiltersParams(searchFilter);

    const response = await this.httpClient.get<GetInvoicesResponse>('/invoices', params);
    return { data: response.data };
  }

  /**
   * Obtiene facturas pendientes de confirmar en Holded (con erp_id pero sin erp_number)
   * GET /api/v1/invoices?advanced_filters[filters][0][column]=erp_id&advanced_filters[filters][0][operator]=is_not_null&advanced_filters[filters][1][column]=erp_number&advanced_filters[filters][1][operator]=is_null&advanced_filters[logic]=AND
   */
  async getPendingConfirmHoldedInvoices(): Promise<{ data: Invoice[] }> {
    const searchFilter = {
      filters: [
        {
          column: 'erp_id',
          operator: 'is_not_null',
        },
        {
          column: 'erp_number',
          operator: 'is_null',
        },
      ],
      logic: 'AND',
    };

    const params = buildAdvancedFiltersParams(searchFilter);

    const response = await this.httpClient.get<GetInvoicesResponse>('/invoices', params);
    return { data: response.data };
  }

  /**
   * Obtiene facturas sin erp_id ni erp_number (pendientes de registrar en Contalink)
   */
  async getPendingContalinkInvoices(): Promise<{ data: Invoice[] }> {
    const searchFilter = {
      filters: [
        { column: 'erp_id', operator: 'is_null' },
        { column: 'erp_number', operator: 'is_null' },
      ],
      logic: 'AND',
    };

    const params = buildAdvancedFiltersParams(searchFilter);
    const response = await this.httpClient.get<GetInvoicesResponse>('/invoices', params);
    return { data: response.data };
  }

  /**
   * Genera facturas en Holded (Paso 1 - Creación)
   * Busca facturas sin pdf_url, crea documentos en Holded y genera PDFs
   * POST /api/v1/invoices/holded
   */
  async generateHoldedInvoices(): Promise<{ data: Invoice[] }> {
    const response = await this.httpClient.post<{ data: Invoice[] }>('/invoices/holded', {});
    return response;
  }

  /**
   * Confirma facturas en Holded y procesa pagos (Paso 2 - Confirmación)
   * Busca facturas con erp_id pero sin erp_number, finaliza en Holded y crea payouts
   * POST /api/v1/invoices/holded/real
   */
  async confirmHoldedInvoices(): Promise<void> {
    await this.httpClient.post<void>('/invoices/holded/real', {});
  }

  /**
   * Registra una factura en Contalink
   * POST /api/v1/invoices/{id}/contalink
   */
  async postInvoiceToContalink(
    invoiceId: number,
    data: {
      erp_number: string;
      erp_id: string;
      grand_total: number;
      pdf: File;
    },
  ): Promise<Invoice> {
    const formData = new FormData();
    formData.append('erp_number', data.erp_number);
    formData.append('erp_id', data.erp_id);
    formData.append('grand_total', data.grand_total.toString());
    formData.append('pdf', data.pdf);

    return await this.httpClient.post<Invoice>(`/invoices/${invoiceId}/contalink`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }
}
